/**
 * Document Type Constants
 * 
 * This file provides a single source of truth for document types across the application.
 * It matches the DocumentClass enum from fenero/schemas/document.py
 * 
 * IMPORTANT: Keep this in sync with fenero/schemas/document.py DocumentClass enum
 * Values use MISMO naming conventions from the backend.
 */

export const DocumentType = {
  // Tax Forms (IRS documents use MISMO IRS document type naming)
  FORM_1040: 'IRS1040',
  SCHEDULE_B: 'IRS1040ScheduleB',
  SCHEDULE_C: 'IRS1040ScheduleC',
  SCHEDULE_D: 'IRS1040ScheduleD',
  SCHEDULE_E: 'IRS1040ScheduleE',
  SCHEDULE_F: 'IRS1040ScheduleF',
  SCHEDULE_K1_1120S: 'IRS1120SScheduleK1',
  SCHEDULE_K1_1065: 'IRS1065ScheduleK1',
  FORM_1099_NEC: 'IRS1099NEC',
  FORM_4506T: 'IRS4506T',
  FORM_1120: 'IRS1120',
  FORM_1120S: 'IRS1120S',
  FORM_1065: 'IRS1065',
  FORM_8825: 'IRS8825',

  // Income Documents
  W2: 'IRSW2',
  PAY_STUB: 'PayStub',
  PROFIT_LOSS_STATEMENT: 'ProfitLossStatement',
  VOE: 'VerificationOfEmployment',

  // Banking
  BANK_STATEMENT: 'BankStatement',

  // Non-Employment Income / Benefits
  SOCIAL_SECURITY_AWARD_LETTER: 'SocialSecurityAwardLetter',
  PENSION_STATEMENT: 'BenefitPlanDistributionStatement:Pension',
  DISABILITY_AWARD_LETTER: 'VerificationOfIncome',
  DIVORCE_DECREE: 'DivorceDecree',
  CHILD_SUPPORT_ORDER: 'VerificationOfDependentCare',
  TRUST_INCOME_STATEMENT: 'TrustIncomeStatement',
  FORM_1099_R: 'IRS1099R',
  FORM_1099_DIV: 'IRS1099DIV',
  FORM_1099_INT: 'IRS1099INT',
  FORM_1099_G: 'IRS1099G',

  // VA / Military Documents
  VA_CERTIFICATE_OF_ELIGIBILITY: 'VACertificateOfEligibility',
  DD214: 'MilitaryDischarge',
  LEAVE_EARNINGS_STATEMENT: 'MilitaryLeaveEarningsStatement',

  // Asset / Down Payment Verification
  GIFT_LETTER: 'GiftLetter',
  VERIFICATION_OF_DEPOSIT: 'VerificationOfDeposit',
  VERIFICATION_OF_RENT: 'VerificationOfMortgageOrRent',
  RETIREMENT_ACCOUNT_STATEMENT: 'RetirementAccountStatement',

  // Other
  TAX_FORM_OTHER: 'TaxReturn',
  OTHER_FINANCIAL: 'Other',
  UNKNOWN: 'Unknown',
} as const

export type DocumentType = typeof DocumentType[keyof typeof DocumentType]

/**
 * Document type metadata for UI display
 */
export interface DocumentTypeInfo {
  value: string
  label: string
  category: 'tax' | 'income' | 'banking' | 'property' | 'other'
}

/**
 * All document types with labels and categories for UI rendering
 * Matches fenero/schemas/document.py DocumentClass enum with MISMO naming
 */
export const DOCUMENT_TYPES: DocumentTypeInfo[] = [
  // Tax Forms (IRS)
  { value: DocumentType.FORM_1040, label: 'Form 1040 (Tax Return)', category: 'tax' },
  { value: DocumentType.SCHEDULE_B, label: 'Schedule B (Interest/Dividends)', category: 'tax' },
  { value: DocumentType.SCHEDULE_C, label: 'Schedule C (Business Income)', category: 'tax' },
  { value: DocumentType.SCHEDULE_D, label: 'Schedule D (Capital Gains)', category: 'tax' },
  { value: DocumentType.SCHEDULE_E, label: 'Schedule E (Rental Income)', category: 'tax' },
  { value: DocumentType.SCHEDULE_F, label: 'Schedule F (Farm Income)', category: 'tax' },
  { value: DocumentType.SCHEDULE_K1_1120S, label: 'Schedule K-1 (1120S - S-Corp)', category: 'tax' },
  { value: DocumentType.SCHEDULE_K1_1065, label: 'Schedule K-1 (1065 - Partnership)', category: 'tax' },
  { value: DocumentType.FORM_1099_NEC, label: 'Form 1099-NEC', category: 'tax' },
  { value: DocumentType.FORM_1099_R, label: 'Form 1099-R (Retirement)', category: 'tax' },
  { value: DocumentType.FORM_1099_DIV, label: 'Form 1099-DIV (Dividends)', category: 'tax' },
  { value: DocumentType.FORM_1099_INT, label: 'Form 1099-INT (Interest)', category: 'tax' },
  { value: DocumentType.FORM_1099_G, label: 'Form 1099-G (Government)', category: 'tax' },
  { value: DocumentType.FORM_4506T, label: 'Form 4506-T (Tax Transcript)', category: 'tax' },
  { value: DocumentType.FORM_1120, label: 'Form 1120 (Corporate Return)', category: 'tax' },
  { value: DocumentType.FORM_1120S, label: 'Form 1120S (S-Corp Return)', category: 'tax' },
  { value: DocumentType.FORM_1065, label: 'Form 1065 (Partnership Return)', category: 'tax' },
  { value: DocumentType.FORM_8825, label: 'Form 8825 (Rental Real Estate)', category: 'tax' },
  { value: DocumentType.TAX_FORM_OTHER, label: 'Other Tax Form', category: 'tax' },

  // Income Documents
  { value: DocumentType.W2, label: 'W-2 (Wage Statement)', category: 'income' },
  { value: DocumentType.PAY_STUB, label: 'Pay Stub', category: 'income' },
  { value: DocumentType.PROFIT_LOSS_STATEMENT, label: 'Profit & Loss Statement', category: 'income' },
  { value: DocumentType.VOE, label: 'Verification of Employment', category: 'income' },
  { value: DocumentType.LEAVE_EARNINGS_STATEMENT, label: 'Leave & Earnings Statement (Military)', category: 'income' },

  // Banking
  { value: DocumentType.BANK_STATEMENT, label: 'Bank Statement', category: 'banking' },
  { value: DocumentType.VERIFICATION_OF_DEPOSIT, label: 'Verification of Deposit', category: 'banking' },
  { value: DocumentType.RETIREMENT_ACCOUNT_STATEMENT, label: 'Retirement Account Statement', category: 'banking' },

  // Benefits / Non-Employment Income
  { value: DocumentType.SOCIAL_SECURITY_AWARD_LETTER, label: 'Social Security Award Letter', category: 'income' },
  { value: DocumentType.PENSION_STATEMENT, label: 'Pension Statement', category: 'income' },
  { value: DocumentType.DISABILITY_AWARD_LETTER, label: 'Disability Award Letter', category: 'income' },
  { value: DocumentType.DIVORCE_DECREE, label: 'Divorce Decree', category: 'income' },
  { value: DocumentType.CHILD_SUPPORT_ORDER, label: 'Child Support Order', category: 'income' },
  { value: DocumentType.TRUST_INCOME_STATEMENT, label: 'Trust Income Statement', category: 'income' },
  { value: DocumentType.GIFT_LETTER, label: 'Gift Letter', category: 'other' },

  // VA / Military
  { value: DocumentType.VA_CERTIFICATE_OF_ELIGIBILITY, label: 'VA Certificate of Eligibility', category: 'other' },
  { value: DocumentType.DD214, label: 'DD-214 (Military Discharge)', category: 'other' },

  // Property
  { value: DocumentType.VERIFICATION_OF_RENT, label: 'Verification of Rent/Mortgage', category: 'property' },

  // Other
  { value: DocumentType.OTHER_FINANCIAL, label: 'Other Financial Document', category: 'other' },
  { value: DocumentType.UNKNOWN, label: 'Unknown', category: 'other' },
]

/**
 * Get document type info by value
 */
export function getDocumentTypeInfo(value: string): DocumentTypeInfo | undefined {
  return DOCUMENT_TYPES.find((dt) => dt.value === value)
}

/**
 * Get document type label by value
 */
export function getDocumentTypeLabel(value: string): string {
  const info = getDocumentTypeInfo(value)
  return info?.label || value
}

/**
 * Group document types by category
 */
export function getDocumentTypesByCategory(): Record<string, DocumentTypeInfo[]> {
  const grouped: Record<string, DocumentTypeInfo[]> = {}
  for (const docType of DOCUMENT_TYPES) {
    if (!grouped[docType.category]) {
      grouped[docType.category] = []
    }
    grouped[docType.category].push(docType)
  }
  return grouped
}
